import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import IconContainer from "@/components/ui/icon-container";
import UserAvatar from "@/components/controls/UserAvatar";
import NotFoundPage from "@/components/layouts/NotFoundPage";
import {
  ChevronDown,
  ChevronRight,
  Contracts
} from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/utils/utils";
import { useUsers } from "@/entities/users/hooks";
import { ReservedUserIds } from "@/entities/users/types";
import { useContract } from "@/pages/contracts/hooks";
import {
  formatCurrency,
  formatDateRange,
  formatDateShort,
  getBillingFrequencyLabel
} from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { groupByBundle } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/bundleRows";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { versionFullName } from "./versionLabels";
import { useTermsVersionDetail } from "./hooks";
import {
  TermsVersionBundle,
  TermsVersionItem,
  TermsVersionStatus
} from "./types";

// Module scope: a static, read-only header set, so it is built once rather than
// on every render.
interface VersionTermsColumn {
  key: string;
  align: string;
}

const COLUMNS: VersionTermsColumn[] = [
  { key: "contractTerms.table.serviceItemName", align: "text-left" },
  { key: "contractTerms.table.customServiceDuration", align: "text-left" },
  { key: "contractTerms.table.quantity", align: "text-right" },
  { key: "contractTerms.table.price", align: "text-left" },
  { key: "contractTerms.table.customBillingFrequency", align: "text-left" },
  { key: "contractTerms.table.discountAmount", align: "text-right" },
  { key: "contractTerms.table.total", align: "text-right" }
];

// The terms as they stood at one saved version, on their own page — a drill-down
// from the timeline rather than a dialog. Read-only throughout: every value comes
// from the version snapshot, so a version whose services were deleted from the
// live set months ago still renders in full.
export const VersionTermsPage: React.FC = () => {
  const { t } = useTranslation();
  // Same idiom as the changes table, so neither depends on a mount-count
  // argument holding true.
  const titleId = React.useId();
  const { contractId, versionId } = useParams<{
    contractId: string;
    versionId: string;
  }>();

  const { data: contract } = useContract(contractId);
  const { data, isLoading, isError } = useTermsVersionDetail(
    contractId,
    versionId
  );
  // The detail payload carries only the author's id; the version list resolves
  // the name for the timeline, but a deep link to this page has no such context.
  const { users: authors } = useUsers(data?.createdBy ? [data.createdBy] : [], {
    enabled: !!data?.createdBy
  });
  // Collapsed by default, as on the contract page.
  const [expandedBundleIds, setExpandedBundleIds] = useState<string[]>([]);
  // Off, a version's lines show flat, as the contract page's do. Waited for, so
  // a cached version doesn't show flat and then regroup.
  const { enabled: bundlesEnabled, loading: isBundlesFlagLoading } =
    useFeatureFlag(FeatureFlags.CRM_11089_PRODUCT_BUNDLES);

  if (isError) return <NotFoundPage data-test="version-terms-404" />;

  if (isLoading || isBundlesFlagLoading || !data) {
    return (
      <div className="flex items-center justify-center h-full w-full min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  const currency = data.financials.currency ?? contract?.currency ?? null;
  const title = versionFullName(data, t);
  // `useUsers` resolves an id it cannot find to the `unknownUser` sentinel, not
  // to undefined, so an unresolved author has to be recognised by its id — on
  // its display name it would read as a real user called "Unknown".
  const resolved = authors[0];
  const author =
    resolved && resolved.id !== ReservedUserIds.UNKNOWN_USER_ID
      ? resolved
      : undefined;
  const authorName = author?.displayName;

  const blocks = groupByBundle(
    data.items,
    bundlesEnabled ? (data.bundles ?? []) : [],
    ({ contractBundleId }) => contractBundleId
  );
  const hasBundles = blocks.some((block) => block.kind === "bundle");
  // A bundled line's stored total is after its share of the bundle discount;
  // the line shows its own total, and the discount shows on the bundle.
  const ownTotal = (item: TermsVersionItem) =>
    item.totalLocal == null
      ? null
      : item.totalLocal + (item.bundleDiscountLocal ?? 0);
  const sumOf = (values: (number | null)[]) =>
    values.some((value) => value == null)
      ? null
      : values.reduce<number>((sum, value) => sum + (value ?? 0), 0);

  const toggleBundle = (id: string) =>
    setExpandedBundleIds((current) =>
      current.includes(id)
        ? current.filter((expanded) => expanded !== id)
        : [...current, id]
    );

  const renderLine = (
    item: TermsVersionItem,
    index: number,
    inBundle: boolean
  ) => (
    <tr
      key={item.revenueItemId ?? `${item.serviceItemId}-${index}`}
      className={cn(
        "border-b border-border-border1 last:border-b-0",
        inBundle && "bg-surface-recessed"
      )}
    >
      {hasBundles ? <td /> : null}
      <td className="px-3 py-3 align-top">
        <Text variant="body">{item.serviceItemName ?? "—"}</Text>
      </td>
      <td className="px-3 py-3 align-top">
        <Text variant="smBodySecondary">
          {item.serviceStartDate
            ? formatDateRange(
                item.serviceStartDate,
                item.serviceEndDate ?? ""
              ) || formatDateShort(item.serviceStartDate)
            : "—"}
        </Text>
      </td>
      <td className="px-3 py-3 align-top text-right">
        <Text variant="body">{item.quantity ?? "—"}</Text>
      </td>
      <td className="px-3 py-3 align-top">
        <Text variant="body">
          {formatCurrency(item.pricePerUnit, currency)}
        </Text>
      </td>
      <td className="px-3 py-3 align-top">
        <Text variant="smBodySecondary">
          {getBillingFrequencyLabel(item.customBillingFrequency, t)}
        </Text>
      </td>
      <td className="px-3 py-3 align-top text-right">
        <Text variant="body">
          {/* `!= null`, not truthiness: a recorded discount of 0 is a
              discount of zero, not an absent one, and the row's other cells
              already use `?? "—"` for exactly that. */}
          {item.discountAmount != null
            ? item.isDiscountPercentage
              ? `${item.discountAmount}%`
              : formatCurrency(item.discountAmount, currency)
            : "—"}
        </Text>
      </td>
      <td className="px-3 py-3 align-top text-right">
        <Text variant="body">
          {/* Contract-currency only, like the footer — `currency` here is
              the snapshot's, so the account-currency twin would be labelled
              wrongly. See TermsVersionFinancials in types.ts. */}
          {formatCurrency(
            inBundle ? ownTotal(item) : item.totalLocal,
            currency
          )}
        </Text>
      </td>
    </tr>
  );

  const renderBundle = (
    bundle: TermsVersionBundle,
    lines: TermsVersionItem[],
    index: number
  ) => {
    const id = bundle.contractBundleId ?? `bundle-${index}`;
    const isExpanded = expandedBundleIds.includes(id);
    const hasDiscount = (bundle.discountAmount ?? 0) > 0;
    const discountedTotal = sumOf(lines.map((line) => line.totalLocal));
    // Struck through only when it differs, as on the contract page.
    const originalTotal = hasDiscount ? sumOf(lines.map(ownTotal)) : null;
    return (
      <React.Fragment key={id}>
        <tr
          className="border-b border-border-border1 last:border-b-0"
          data-test={`version-bundle-row-${id}`}
        >
          <td className="px-1 align-top py-3">
            <IconButton
              variant="iconNoBg"
              size="sm"
              iconColor="primary"
              onClick={() => toggleBundle(id)}
              aria-expanded={isExpanded}
              aria-label={t("contractTerms.bundle.toggle", {
                name: bundle.name ?? ""
              })}
              data-test={`version-bundle-toggle-${id}`}
            >
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </IconButton>
          </td>
          <td className="px-3 py-3 align-top">
            <div className="flex min-w-0 flex-col gap-0.5">
              <Text variant="label" className="truncate">
                {bundle.name ?? "—"}
              </Text>
              <Text variant="smBodySecondary">
                {t("contractTerms.bundle.serviceCount", {
                  count: lines.length
                })}
              </Text>
            </div>
          </td>
          {/* No period, quantity, price or billing: a bundle's lines can
              each have their own. */}
          <td />
          <td />
          <td />
          <td />
          <td
            className="px-3 py-3 align-top text-right"
            data-test={`version-bundle-discount-${id}`}
          >
            <Text variant="body">
              {hasDiscount
                ? bundle.isDiscountPercentage
                  ? `${bundle.discountAmount}%`
                  : formatCurrency(bundle.discountAmount, currency)
                : null}
            </Text>
          </td>
          <td
            className="px-3 py-3 align-top text-right"
            data-test={`version-bundle-total-${id}`}
          >
            <div className="flex items-center justify-end gap-2">
              {originalTotal != null && originalTotal !== discountedTotal ? (
                <Text variant="bodyTertiary" className="line-through">
                  {formatCurrency(originalTotal, currency)}
                </Text>
              ) : null}
              <Text variant="body">
                {formatCurrency(discountedTotal, currency)}
              </Text>
            </div>
          </td>
        </tr>
        {isExpanded
          ? lines.map((line, lineIndex) => renderLine(line, lineIndex, true))
          : null}
      </React.Fragment>
    );
  };

  const figure = (labelKey: string, value: number | null, strong = false) => (
    <div className="flex items-center justify-between py-1">
      <Text variant="bodySecondary">{t(labelKey)}</Text>
      <Text variant={strong ? "lgSubtitle" : "body"}>
        {formatCurrency(value, currency)}
      </Text>
    </div>
  );

  return (
    <div className="flex flex-col gap-4 w-full max-w-[1800px] mx-auto p-6">
      {/* `min-w-0` belongs on each flex item that truncates, not only on the
          row: an item's `min-width: auto` is what blocks it shrinking below its
          content, and `truncate` sets no min-width of its own. On the contract
          name the item is the Link, not the Text inside it, so the Link is what
          needs it. The icons take `shrink-0` so the names yield first — without
          that, flex compresses the chevrons alongside the text it should be
          clipping. */}
      <div className="flex items-center gap-2 min-w-0">
        <IconContainer size="5" color="secondary" className="shrink-0">
          <Contracts />
        </IconContainer>
        <Link to="/contracts" className="shrink-0">
          <Text variant="bodySecondary">
            {t("contracts.termsHistory.breadcrumbContracts")}
          </Text>
        </Link>
        <IconContainer size="sm" color="tertiary" className="shrink-0">
          <ChevronRight />
        </IconContainer>
        <Link
          to={`/contracts/${contractId}?tab=termsAndServices`}
          className="min-w-0"
        >
          <Text variant="bodySecondary" className="truncate">
            {contract?.name ?? ""}
          </Text>
        </Link>
        <IconContainer size="sm" color="tertiary" className="shrink-0">
          <ChevronRight />
        </IconContainer>
        <Text variant="body" className="truncate min-w-0">
          {title}
        </Text>
      </div>

      <div className="flex flex-col gap-1">
        <Text variant="smSubtitle" id={titleId}>
          {t("contracts.termsHistory.pageTitle")}
        </Text>
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex items-center gap-1.5">
            <UserAvatar
            userData={author}
            size="S"
            variant="identity"
            className="shrink-0"
          />
            <Text variant="smBodySecondary">
              {authorName
                ? t("contracts.termsHistory.savedBy", { name: authorName })
                : t("contracts.termsHistory.savedByUnknown")}
            </Text>
          </div>
          {data.createdAt ? (
            <Text variant="smBodySecondary">
              {formatDateShort(data.createdAt)}
            </Text>
          ) : null}
          {/* A withdrawn version was cancelled before its effective date
              arrived, so naming that date here would state a day these terms
              are due to take effect — the one thing that will never happen to
              them. It says when they were cancelled instead. */}
          {data.status === TermsVersionStatus.WITHDRAWN ? (
            <>
              {data.cancelledAt ? (
                <Text
                  variant="smBodySecondary"
                  data-test="version-cancelled-on"
                >
                  {t("contracts.termsHistory.cancelledOn", {
                    date: formatDateShort(data.cancelledAt)
                  })}
                </Text>
              ) : null}
              {/* No `cancelledByName` on the detail payload, unlike the list
                  summary, so this names the date and the reason only. */}
              {data.cancellationReason ? (
                <Text
                  variant="smBodyTertiary"
                  className="break-words"
                  data-test="version-cancellation-reason"
                >
                  {data.cancellationReason}
                </Text>
              ) : null}
            </>
          ) : data.effectiveDate ? (
            <Text variant="smBodySecondary" data-test="version-effective-date">
              {t("contracts.termsHistory.effectiveDate", {
                date: formatDateShort(data.effectiveDate)
              })}
            </Text>
          ) : null}
        </div>
      </div>

      <div className="rounded-xl border border-border-widget-card bg-surface-elevation0 overflow-hidden">
        <table className="w-full" aria-labelledby={titleId}>
          <thead>
            <tr className="border-b border-border-border1">
              {hasBundles ? (
                <th
                  scope="col"
                  className="w-8"
                  aria-label={t("contractTerms.table.expand")}
                />
              ) : null}
              {COLUMNS.map(({ key, align }) => (
                <th key={key} className={`px-3 py-2 font-normal ${align}`}>
                  <Text variant="smBodySecondary">{t(key)}</Text>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {blocks.map((block, index) =>
              block.kind === "item"
                ? renderLine(block.item, index, false)
                : renderBundle(block.bundle, block.lines, index)
            )}
          </tbody>
        </table>

        <div className="flex flex-col border-t border-border-border1 px-4 py-3">
          <Text variant="bodyTertiary">
            {t("contractTerms.calculatedValues")}
          </Text>
          {/* Contract-currency only, with no fallback to the account-currency
              twin — see the pair semantics on `TermsVersionFinancials` in
              types.ts. TCV and CMRR are the two where that is load-bearing. */}
          {figure("contractTerms.mrr", data.financials.mrrLocal)}
          {figure("contractTerms.cmrr", data.financials.cmrrLocal)}
          {figure(
            "contractTerms.acv",
            data.financials.annualContractValueLocal
          )}
          {figure(
            "contractTerms.tcv",
            data.financials.totalContractValueLocal,
            true
          )}
        </div>
      </div>
    </div>
  );
};
